import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, RefreshCw, Printer, AlertTriangle, CheckCircle2, Clock, XCircle, Search, Pencil, Loader2, Ban, Activity, ChevronDown, ChevronUp, Zap, TrendingUp, Send } from 'lucide-react';
import { notify } from '@/lib/notify';
import { useAuth } from '@/context/AuthContext';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('pos_token')}` });

const STATUS_MAP = {
  FINISHED: { label: 'Aprobada', color: 'bg-green-500/20 text-green-600 dark:text-green-400', icon: CheckCircle2 },
  REGISTERED: { label: 'Pendiente', color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400', icon: Clock },
  PROCESSING: { label: 'Procesando', color: 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400', icon: Loader2 },
  // 🔒 MULTIPROD-ASYNC-2026-06-23 — XML firmado por Multiprod, en cola DGII.
  // No es Procesando (retry transitorio): es flujo asíncrono nuevo del portal.
  EN_PROCESO: { label: 'En proceso DGII', color: 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400', icon: Loader2 },
  CONTINGENCIA: { label: 'Contingencia', color: 'bg-amber-500/20 text-amber-600 dark:text-amber-400', icon: AlertTriangle },
  CONTINGENCIA_MANUAL: { label: 'Cont. Manual', color: 'bg-gray-500/20 text-gray-600 dark:text-gray-400', icon: Ban },
  REJECTED: { label: 'Rechazada', color: 'bg-red-500/20 text-red-600 dark:text-red-400', icon: XCircle },
  ERROR: { label: 'Error', color: 'bg-red-500/20 text-red-600 dark:text-red-400', icon: XCircle },
};

const ECF_TYPES = [
  { value: 'E31', label: 'E31 - Crédito Fiscal' },
  { value: 'E32', label: 'E32 - Consumo Final' },
  { value: 'E34', label: 'E34 - Nota de Crédito' },
  { value: 'E44', label: 'E44 - Regímenes Especiales' },
  { value: 'E45', label: 'E45 - Gubernamental' },
];

export default function EcfDashboard({ data }) {
  const { user, isAdmin } = useAuth();
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, bill: null, newType: '' });
  const [onlyMine, setOnlyMine] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (data?.bills) {
      setBills(data.bills);
      setSummary(data.summary || {});
    }
  }, [data]);

  const getStatus = (bill) => {
    const s = (bill.ecf_status || '').toUpperCase();
    if (s === 'REJECTED') return 'REJECTED';
    if (s === 'FINISHED') return 'FINISHED';
    if (s === 'PROCESSING') return 'PROCESSING';
    if (s === 'EN_PROCESO') return 'EN_PROCESO';
    if (s === 'REGISTERED') return 'REGISTERED';
    if (s === 'CONTINGENCIA') {
      const err = (bill.ecf_error || '').toLowerCase();
      const forcedByPayment = Array.isArray(bill.payments) && bill.payments.some(
        (p) => p && (p.skip_ecf === true || p.force_contingency === true)
      );
      if (err.includes('contingencia_manual') || bill.force_contingency === true || forcedByPayment) {
        return 'CONTINGENCIA_MANUAL';
      }
      return 'CONTINGENCIA';
    }
    if (bill.ecf_reject_reason && s !== 'FINISHED') return 'REJECTED';
    return STATUS_MAP[s] ? s : 'ERROR';
  };

  const filteredBills = bills.filter(b => {
    if (onlyMine && user?.id && b.cashier_id !== user.id && b.waiter_id !== user.id) return false;
    if (filter !== 'all' && getStatus(b) !== filter) return false;
    // FIX 5: búsqueda en tiempo real (Transaction #, e-NCF, RNC, Razón Social, Monto)
    if (search) {
      const s = search.trim().toLowerCase();
      if (!s) return true;
      const totalStr = String(b.total ?? '');
      const totalFmt = (b.total ?? 0).toLocaleString('es-DO', { minimumFractionDigits: 2 }).toLowerCase();
      const matches = (
        String(b.transaction_number ?? '').includes(s) ||
        `t-${b.transaction_number ?? ''}`.toLowerCase().includes(s) ||
        String(b.ecf_encf || '').toLowerCase().includes(s) ||
        String(b.ncf || '').toLowerCase().includes(s) ||
        String(b.fiscal_id || '').toLowerCase().includes(s) ||
        String(b.razon_social || '').toLowerCase().includes(s) ||
        String(b.customer_name || '').toLowerCase().includes(s) ||
        totalStr.includes(s.replace(/[^\d.]/g, '')) ||
        totalFmt.includes(s)
      );
      if (!matches) return false;
    }
    return true;
  });

  const handleRefreshStatus = async (billId) => {
    const bill = bills.find(b => b.id === billId);
    const provider = bill?.ecf_provider || '';

    // If bill has no provider set (ERROR bills), or provider is multiprod/thefactory, the backend
    // will fallback to system provider. We still call it so ERROR bills get a clear message.
    if (provider === 'multiprod' || provider === 'thefactory') {
      notify.info(`Status actual: ${bill?.ecf_status || 'Sin status'} (via ${provider})`);
      return;
    }
    
    try {
      const r = await fetch(`${API}/api/ecf/refresh-status/${billId}`, { headers: hdrs() });
      const d = await r.json();
      if (d.ok) {
        const msg = d.message || `Status: ${d.status}${d.reject_reason ? ' — ' + d.reject_reason : ''}`;
        notify.success(msg);
        const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
        const fresh = await res.json();
        setBills(fresh.bills || []);
        setSummary(fresh.summary || {});
      } else {
        notify.error(d.message || 'Error consultando status');
      }
    } catch { notify.error('Error de conexión'); }
  };

  // 🔒 ECF-EMIT-PENDING-2026-06-24 — Emisión manual de UN e-CF que se quedó sin
  // emitir (pipeline original no se disparó por config rota). Candados FUERTES
  // en backend: status==paid Y sin ecf_encf/ecf_status. UI deshabilita el botón
  // mientras está en vuelo para evitar doble-click → doble emisión.
  const [emittingId, setEmittingId] = useState(null);

  const isPendingEmission = (b) => {
    const status = String(b?.status || '').toLowerCase();
    const hasEncf = !!(b?.ecf_encf && String(b.ecf_encf).trim());
    const hasEcfStatus = !!(b?.ecf_status && String(b.ecf_status).trim());
    const hasAlanube = !!b?.ecf_alanube_id;
    return status === 'paid' && !hasEncf && !hasEcfStatus && !hasAlanube;
  };

  const handleEmitPending = async (b) => {
    const total = (b.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });
    if (!window.confirm(`¿Emitir e-CF para T-${b.transaction_number} por RD$ ${total}?\n\nSe asignará un e-NCF nuevo y se enviará a DGII. Esta acción no se puede deshacer.`)) {
      return;
    }
    setEmittingId(b.id);
    try {
      const r = await fetch(`${API}/api/ecf/emit-pending/${b.id}`, {
        method: 'POST', headers: hdrs(),
      });
      const d = await r.json();
      if (r.status === 400 || r.status === 404) {
        notify.error(d.detail || 'No se pudo emitir', { duration: 8000 });
      } else if (d.ok) {
        const encf = d.encf || '(sin e-NCF)';
        const desc = [
          `Estado: ${d.ecf_status || '—'}`,
          d.provider ? `Provider: ${d.provider}` : null,
          d.qr ? `QR: ${d.qr}` : null,
        ].filter(Boolean).join(' · ');
        notify.success(`✅ e-CF emitido: ${encf}`, { description: desc, duration: 10000 });
      } else {
        notify.error(d.error || d.motivo || 'Error emitiendo e-CF', { duration: 9000 });
      }
      // Refrescar dashboard (tanto si fue éxito como error: el bill pudo cambiar).
      try {
        const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
        const fresh = await res.json();
        setBills(fresh.bills || []);
        setSummary(fresh.summary || {});
      } catch (_err) { /* refresh es best-effort */ }
    } catch {
      notify.error('Error de conexión');
    }
    setEmittingId(null);
  };

  // 🔒 FIX-004-RECUPERABLE-AUTO-2026-06-25 — Drena el backlog Error/REJECTED/
  // REINTENTAR (generado ANTES del fix del 004). Procesa secuencialmente
  // reusando /resend y /emit-pending. El poller luego confirma los EN_PROCESO.
  const [reprocessing, setReprocessing] = useState(false);

  const handleReprocessBacklog = async () => {
    if (!isAdmin) return;
    if (!window.confirm(
      '¿REPROCESAR REZAGO?\n\n' +
      'Esto va a re-disparar el envío de TODAS las facturas atascadas en ' +
      'Error / REINTENTAR / REJECTED / CONTINGENCIA (hasta 50 por corrida) ' +
      'reusando sus e-NCFs cuando ya existan, o asignando uno nuevo cuando ' +
      'no. Las ya aceptadas en producción NO se tocan.\n\n' +
      'El poller automáticamente las llevará a FINISHED cuando DGII confirme.'
    )) return;
    setReprocessing(true);
    try {
      const r = await fetch(`${API}/api/ecf/reprocess-backlog?limit=50`, {
        method: 'POST', headers: hdrs(),
      });
      const d = await r.json();
      const total = d.total_candidates || 0;
      const dispatched = (d.dispatched_resend || 0) + (d.dispatched_emit_pending || 0);
      const skipped = (d.skipped_already_prod || 0) + (d.skipped_not_paid || 0);
      notify.success(
        `Rezago procesado: ${dispatched}/${total} despachadas`,
        {
          description: `resend=${d.dispatched_resend || 0} · emit-pending=${d.dispatched_emit_pending || 0} · skipped=${skipped} · errores=${d.errors || 0}. El poller las confirma cada 2 min.`,
          duration: 12000,
        }
      );
      // Refrescar dashboard.
      try {
        const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
        const fresh = await res.json();
        setBills(fresh.bills || []);
        setSummary(fresh.summary || {});
      } catch (_e) { /* best-effort */ }
    } catch {
      notify.error('Error de conexión al reprocesar rezago');
    }
    setReprocessing(false);
  };

  const handleRetry = async (billId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ecf/retry/${billId}`, { method: 'POST', headers: hdrs() });
      const d = await r.json();
      if (d.ok) {
        notify.success(`e-CF reenviado: ${d.encf}`);
        const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
        const fresh = await res.json();
        setBills(fresh.bills || []);
        setSummary(fresh.summary || {});
      } else {
        // d.message ya viene humanizado del backend (humanize_dgii_error)
        notify.error(d.message || 'Error reenviando e-CF', { duration: 9000 });
      }
    } catch { notify.error('Error de conexión'); }
    setLoading(false);
  };

  // 🔒 BUG-RETRY-STUCK-2026-05 — reintento MANUAL para facturas en PROCESSING.
  // Reusa el MISMO e-NCF y el mismo XML guardado (no genera nueva secuencia DGII).
  // Solo Admin / Gerente / Propietario.
  const handleRetryStuck = async (billId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ecf/retry-stuck/${billId}`, { method: 'POST', headers: hdrs() });
      const d = await r.json();
      if (d.ok && d.status === 'aceptado') {
        notify.success(`✅ e-NCF ${d.ecf_encf} aprobado por DGII`, { duration: 8000 });
      } else if (d.ok && d.status === 'en_proceso') {
        // 🔒 MULTIPROD-ASYNC-2026-06-23 — Reintento exitoso → factura firmada por
        // Multiprod y en cola DGII. NO es error; es flujo asíncrono normal.
        notify.info(
          `✍️ e-NCF ${d.ecf_encf} firmado por Multiprod y en cola DGII`,
          {
            description: `id_cola=${d.id_cola || '?'}. Se actualizará automáticamente cuando DGII confirme estado final.`,
            duration: 10000,
          }
        );
      } else if (d.status === 'rechazado') {
        notify.error(`❌ ${d.message || 'Rechazado por DGII'}`, { duration: 10000 });
      } else if (d.status === 'processing') {
        notify.error(`⏳ ${d.message || 'Multiprod aún no responde, intenta de nuevo en 1 min.'}`, { duration: 10000 });
      } else if ((d.ecf_status || '').toUpperCase() === 'EN_PROCESO') {
        // 🔒 MULTIPROD-ASYNC-2026-06-23 — Backend bloqueó el reintento porque la
        // factura ya está firmada por Multiprod y en cola DGII. Mostrar como INFO,
        // no como error: es comportamiento correcto del sistema.
        notify.info(
          'Esta factura ya fue firmada por Multiprod',
          {
            description: 'Está en cola para envío a DGII. No es necesario reintentar — el estado final llegará automáticamente.',
            duration: 10000,
          }
        );
      } else if ((d.ecf_status || '').toUpperCase() === 'FINISHED') {
        notify.info('Esta factura ya fue aceptada por DGII', { duration: 6000 });
      } else {
        notify.error(d.message || d.detail || 'Error reintentando', { duration: 8000 });
      }
      // Refresh dashboard
      const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
      const fresh = await res.json();
      setBills(fresh.bills || []);
      setSummary(fresh.summary || {});
    } catch (err) { notify.error('Error de conexión'); }
    setLoading(false);
  };

  // Reintentar TODAS las facturas atascadas en PROCESSING / CONTINGENCIA.
  const handleRetryStuckAll = async () => {
    if (!window.confirm('¿Reintentar TODAS las facturas atascadas (PROCESSING) reusando sus e-NCFs originales?')) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ecf/retry-stuck-all?since_days=7`, { method: 'POST', headers: hdrs() });
      const d = await r.json();
      const msg = `Reintentados ${d.total_processed}: ${d.accepted} ✅ aprobadas · ${d.rejected} ❌ rechazadas · ${d.still_processing} ⏳ siguen procesando · ${d.errors} errores`;
      notify.success(msg, { duration: 12000 });
      const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
      const fresh = await res.json();
      setBills(fresh.bills || []);
      setSummary(fresh.summary || {});
    } catch { notify.error('Error de conexión'); }
    setLoading(false);
  };

  const handleRetryAll = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ecf/retry-all`, { method: 'POST', headers: hdrs() });
      const d = await r.json();
      let msg = `Reintentos: ${d.success} exitosos, ${d.failed} fallidos de ${d.total}`;
      if (d.skipped_manual > 0) {
        msg += ` · ${d.skipped_manual} omitida(s) (contingencia manual — reintentar individualmente)`;
      }
      if (d.failed > 0 && Array.isArray(d.failed_messages) && d.failed_messages.length > 0) {
        const sample = d.failed_messages.slice(0, 2).map(m => m.message?.slice(0, 80)).join(' | ');
        msg += `\nMotivo más común: ${sample}`;
      }
      notify.success(msg, { duration: 10000 });
      const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
      const fresh = await res.json();
      setBills(fresh.bills || []);
      setSummary(fresh.summary || {});
    } catch { notify.error('Error de conexión'); }
    setLoading(false);
  };

  const handleReprint = async (billId) => {
    // BUG-F17 fix: previous version silently ignored failures from
    // refresh-status and ALWAYS notified "Factura enviada" even when the
    // print agent was offline. Now we surface real errors and refresh the
    // local row with the new reprint_count returned by the backend.
    try {
      await fetch(`${API}/api/ecf/refresh-status/${billId}`, { headers: hdrs() });
    } catch {
      // status refresh is best-effort; printing must still report its own status
    }
    try {
      const r = await fetch(`${API}/api/print/receipt/${billId}/send`, { method: 'POST', headers: hdrs() });
      let d = {};
      try { d = await r.json(); } catch {}
      if (r.ok && d.ok !== false) {
        notify.success('Factura enviada a impresora');
        const copyN = d.reprint_count;
        if (typeof copyN === 'number') {
          setBills(prev => prev.map(b => b.id === billId ? { ...b, reprint_count: copyN } : b));
        }
      } else {
        notify.error(d.detail || d.message || 'Error al imprimir');
      }
    } catch {
      notify.error('Error de conexión al imprimir');
    }
  };

  const handleEditEcfType = async () => {
    if (!editDialog.bill || !editDialog.newType) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/bills/${editDialog.bill.id}/ecf-type`, {
        method: 'PATCH',
        headers: { ...hdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ecf_type: editDialog.newType })
      });
      const d = await r.json();
      if (d.ok) {
        notify.success(d.message || 'Tipo de e-CF actualizado');
        setEditDialog({ open: false, bill: null, newType: '' });
        // Refresh data
        const res = await fetch(`${API}/api/ecf/dashboard`, { headers: hdrs() });
        const fresh = await res.json();
        setBills(fresh.bills || []);
        setSummary(fresh.summary || {});
      } else {
        notify.error(d.detail || d.message || 'Error al actualizar');
      }
    } catch { notify.error('Error de conexión'); }
    setLoading(false);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' }); } catch { return d.slice(0, 16); }
  };

  if (!data) return <p className="text-center text-muted-foreground py-8">Sin datos</p>;

  // ─── Detect e-CF environment from the latest QR ────────────────────────────
  // DGII returns a QR URL with /ecf/ for production or /testecf/ for sandbox.
  // We pick the most recent accepted bill's QR, fall back to ANY bill's QR.
  const envBadge = (() => {
    const sorted = [...bills].sort((a, b) => {
      const ta = new Date(a.paid_at || a.created_at || 0).getTime();
      const tb = new Date(b.paid_at || b.created_at || 0).getTime();
      return tb - ta;
    });
    const withQr = sorted.find((b) => b.ecf_qr || b.ecf_qr_code);
    if (!withQr) {
      return {
        kind: 'unknown',
        label: 'SIN ENVÍOS RECIENTES',
        message: 'Aún no hay facturas con respuesta de DGII. Emite una para detectar el ambiente.',
        bg: 'bg-slate-500/10 border-slate-500/30',
        text: 'text-slate-600 dark:text-slate-300',
        icon: Activity,
      };
    }
    const qr = String(withQr.ecf_qr || withQr.ecf_qr_code || '').toLowerCase();
    const ecfStatus = String(withQr.ecf_status || '').toLowerCase();
    // 🔒 IMPORTANT (2026-05-01): Multiprod has a known cosmetic bug where the
    // returned QR contains "/testecf/" even when the e-CF was successfully
    // accepted in DGII PRODUCTION (verified via DGII Oficina Virtual). Trust
    // ecf_status (accepted/FINISHED) as the source of truth for environment
    // detection, NOT the QR alone.
    const acceptedInProduction = ecfStatus === 'accepted' || ecfStatus === 'finished' || ecfStatus === 'aceptado';
    if (qr.includes('/testecf/') || qr.includes('testecf.')) {
      if (acceptedInProduction) {
        return {
          kind: 'production',
          label: 'PRODUCCIÓN ACTIVA',
          message: 'Las facturas SÍ se registran en DGII producción. (El QR con "/testecf/" es un detalle cosmético del proveedor — el e-CF está aceptado oficialmente.)',
          bg: 'bg-emerald-500/10 border-emerald-500/40',
          text: 'text-emerald-700 dark:text-emerald-400',
          icon: CheckCircle2,
        };
      }
      return {
        kind: 'sandbox',
        label: 'MODO SANDBOX (PRUEBAS)',
        message: 'Las facturas se envían al ambiente de pruebas de DGII. NO se registran fiscalmente. Pídele a tu proveedor la URL de producción antes de operar con clientes reales.',
        bg: 'bg-amber-500/10 border-amber-500/40',
        text: 'text-amber-700 dark:text-amber-400',
        icon: AlertTriangle,
      };
    }
    if (qr.includes('fc.dgii.gov.do/ecf/') || (qr.includes('/ecf/') && !qr.includes('testecf'))) {
      return {
        kind: 'production',
        label: 'PRODUCCIÓN ACTIVA',
        message: 'Las facturas se registran fiscalmente en DGII. Verificado contra el QR oficial.',
        bg: 'bg-emerald-500/10 border-emerald-500/40',
        text: 'text-emerald-700 dark:text-emerald-400',
        icon: CheckCircle2,
      };
    }
    return {
      kind: 'unknown',
      label: 'AMBIENTE NO DETECTADO',
      message: 'No se pudo determinar el ambiente desde la última respuesta DGII.',
      bg: 'bg-slate-500/10 border-slate-500/30',
      text: 'text-slate-600 dark:text-slate-300',
      icon: Activity,
    };
  })();
  const EnvIcon = envBadge.icon;

  return (
    <div className="space-y-5" data-testid="ecf-dashboard">
      {/* 🌐 Environment badge — detected from last DGII QR */}
      <div
        className={`rounded-xl border ${envBadge.bg} px-4 py-3 flex items-center gap-3`}
        data-testid={`ecf-env-badge-${envBadge.kind}`}
      >
        <EnvIcon size={20} className={envBadge.text} />
        <div className="flex-1 min-w-0">
          <p className={`font-oswald font-bold text-sm tracking-wide ${envBadge.text}`} data-testid="ecf-env-label">
            {envBadge.label}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{envBadge.message}</p>
        </div>
      </div>
      {/* 🚨 Rechazos DGII — Alerta visible al tope con motivo completo y botón Reintentar */}
      {(() => {
        const rejectedBills = bills.filter((b) => getStatus(b) === 'REJECTED');
        if (rejectedBills.length === 0) return null;
        return (
          <div
            className="rounded-xl border border-red-500/40 bg-red-500/5 overflow-hidden"
            data-testid="ecf-rejections-alert-card"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-red-500/30 bg-red-500/10">
              <XCircle size={18} className="text-red-500" />
              <h3 className="font-oswald font-bold text-red-600 dark:text-red-400 text-sm uppercase tracking-wide">
                Rechazos DGII · Acción requerida
              </h3>
              <span className="ml-auto text-xs font-bold text-red-500">
                {rejectedBills.length} rechazada{rejectedBills.length !== 1 ? 's' : ''}
              </span>
            </div>
            <ul className="divide-y divide-red-500/20">
              {rejectedBills.slice(0, 5).map((b) => {
                const arStatus = b.ecf_auto_retry_status;
                const arAttempt = b.ecf_auto_retry_attempt || 0;
                const arMax = b.ecf_auto_retry_max || 5;
                const arNextAt = b.ecf_auto_retry_next_at;
                const isAutoRetrying = arStatus === 'pending' && arNextAt;
                let autoRetryLabel = '';
                if (isAutoRetrying) {
                  try {
                    const ms = new Date(arNextAt) - new Date();
                    if (ms > 0) {
                      const mins = Math.floor(ms / 60000);
                      const secs = Math.floor((ms % 60000) / 1000);
                      autoRetryLabel = mins >= 1 ? `${mins}m ${secs}s` : `${secs}s`;
                    } else {
                      autoRetryLabel = 'pronto';
                    }
                  } catch { autoRetryLabel = ''; }
                }
                return (
                <li
                  key={b.id}
                  className="px-4 py-3 flex items-start gap-3 hover:bg-red-500/5 transition-all"
                  data-testid={`ecf-rejection-item-${b.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-sm">T-{b.transaction_number}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {b.ecf_type || '—'}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        {b.ecf_encf || '—'}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs font-semibold">
                        RD$ {(b.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                      {isAutoRetrying && (
                        <Badge
                          className="text-[10px] bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30"
                          data-testid={`auto-retry-badge-${b.id}`}
                        >
                          <RefreshCw size={9} className="mr-1 animate-spin" />
                          Auto-retry {arAttempt}/{arMax}{autoRetryLabel ? ` · en ${autoRetryLabel}` : ''}
                        </Badge>
                      )}
                      {arStatus === 'exhausted' && (
                        <Badge className="text-[10px] bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-500/30">
                          Auto-retry agotado ({arAttempt}/{arMax})
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs mt-1 text-red-600 dark:text-red-400 break-words">
                      <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />
                      {b.ecf_reject_reason || 'Rechazado por DGII sin motivo especificado'}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {formatDate(b.ecf_sent_at || b.paid_at)}
                      {b.ecf_provider ? ` · via ${b.ecf_provider}` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleRetry(b.id)}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                    data-testid={`ecf-rejection-retry-${b.id}`}
                  >
                    <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Reintentar
                  </Button>
                </li>
                );
              })}
            </ul>
            {rejectedBills.length > 5 && (
              <div className="px-4 py-2 text-[11px] text-center text-muted-foreground border-t border-red-500/20">
                + {rejectedBills.length - 5} rechazo(s) adicional(es) — filtra por "Rechazadas" abajo para verlos todos
              </div>
            )}
          </div>
        );
      })()}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <button onClick={() => setFilter('all')} className={`bg-card border rounded-xl p-3 text-center transition-all ${filter === 'all' ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}>
          <FileText size={18} className="text-primary mx-auto mb-1" />
          <p className="font-oswald text-xl font-bold">{summary.total || 0}</p>
          <p className="text-[10px] text-muted-foreground">Total</p>
        </button>
        <button onClick={() => setFilter('FINISHED')} className={`bg-green-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'FINISHED' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-green-500/30'}`}>
          <CheckCircle2 size={18} className="text-green-600 dark:text-green-400 mx-auto mb-1" />
          <p className="font-oswald text-xl font-bold text-green-600 dark:text-green-400">{summary.approved || 0}</p>
          <p className="text-[10px] font-bold text-slate-800 dark:text-green-100">Aprobadas</p>
        </button>
        <button onClick={() => setFilter('REGISTERED')} className={`bg-blue-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'REGISTERED' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-blue-500/30'}`}>
          <Clock size={18} className="text-blue-600 dark:text-blue-400 mx-auto mb-1" />
          <p className="font-oswald text-xl font-bold text-blue-600 dark:text-blue-400">{summary.registered || 0}</p>
          <p className="text-[10px] font-bold text-slate-800 dark:text-blue-100">Pendientes</p>
        </button>
        <button onClick={() => setFilter('PROCESSING')} className={`bg-cyan-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'PROCESSING' ? 'border-cyan-500 ring-2 ring-cyan-500/20' : 'border-cyan-500/30'}`}>
          {(() => {
            const count = bills.filter(b => getStatus(b) === 'PROCESSING').length;
            return (
              <>
                <Loader2 size={18} className={`text-cyan-600 dark:text-cyan-400 mx-auto mb-1 ${count > 0 ? 'animate-spin' : ''}`} />
                <p className="font-oswald text-xl font-bold text-cyan-600 dark:text-cyan-400">{count}</p>
              </>
            );
          })()}
          <p className="text-[10px] font-bold text-slate-800 dark:text-cyan-100">Procesando</p>
        </button>
        {/* 🔒 MULTIPROD-ASYNC-2026-06-23 — KPI nuevo: e-CFs firmados por Multiprod
            y en cola para envío a DGII. Estado intermedio del portal nuevo. */}
        <button onClick={() => setFilter('EN_PROCESO')} className={`bg-indigo-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'EN_PROCESO' ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-indigo-500/30'}`} data-testid="ecf-kpi-en-proceso">
          {(() => {
            const count = bills.filter(b => getStatus(b) === 'EN_PROCESO').length;
            return (
              <>
                <Loader2 size={18} className={`text-indigo-600 dark:text-indigo-400 mx-auto mb-1 ${count > 0 ? 'animate-spin' : ''}`} />
                <p className="font-oswald text-xl font-bold text-indigo-600 dark:text-indigo-400">{count}</p>
              </>
            );
          })()}
          <p className="text-[10px] font-bold text-slate-800 dark:text-indigo-100" title="XML firmado por Multiprod, en cola para envío a DGII">En proceso DGII</p>
        </button>
        <button onClick={() => setFilter('CONTINGENCIA')} className={`bg-amber-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'CONTINGENCIA' ? 'border-amber-500 ring-2 ring-amber-500/20' : 'border-amber-500/30'}`}>
          <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 mx-auto mb-1" />
          <p className="font-oswald text-xl font-bold text-amber-600 dark:text-amber-400">{summary.contingencia || 0}</p>
          <p className="text-[10px] font-bold text-slate-800 dark:text-amber-100">Contingencia</p>
        </button>
        <button onClick={() => setFilter('CONTINGENCIA_MANUAL')} className={`bg-gray-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'CONTINGENCIA_MANUAL' ? 'border-gray-500 ring-2 ring-gray-500/20' : 'border-gray-500/30'}`}>
          <Ban size={18} className="text-gray-500 dark:text-gray-400 mx-auto mb-1" />
          <p className="font-oswald text-xl font-bold text-gray-600 dark:text-gray-400">{summary.contingencia_manual ?? bills.filter(b => getStatus(b) === 'CONTINGENCIA_MANUAL').length}</p>
          <p className="text-[10px] font-bold text-slate-800 dark:text-gray-300">Cont. Manual</p>
        </button>
        <button onClick={() => setFilter('REJECTED')} className={`bg-red-500/10 border rounded-xl p-3 text-center transition-all ${filter === 'REJECTED' ? 'border-red-500 ring-2 ring-red-500/20' : 'border-red-500/30'}`}>
          <XCircle size={18} className="text-red-600 dark:text-red-400 mx-auto mb-1" />
          <p className="font-oswald text-xl font-bold text-red-600 dark:text-red-400">{summary.rejected || 0}</p>
          <p className="text-[10px] font-bold text-slate-800 dark:text-red-100">Rechazadas</p>
        </button>
      </div>

      {/* Filter: Only mine + FIX 5: búsqueda en tiempo real */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={onlyMine} onChange={e => setOnlyMine(e.target.checked)} className="rounded" data-testid="only-mine-filter" />
          Solo mis facturas en proceso
        </label>
        {/* 🔒 BUG-RETRY-STUCK-2026-05 — boton global para Admin que reintenta TODAS las atascadas (PROCESSING).
            Conta procesando con bills.filter() en vez de summary.processing porque el backend no envia ese campo
            consistentemente; este es el mismo metodo que usa el KPI card. */}
        {isAdmin && (() => {
          const procCount = bills.filter(b => getStatus(b) === 'PROCESSING').length;
          if (procCount === 0) return null;
          return (
            <Button
              size="sm"
              onClick={handleRetryStuckAll}
              disabled={loading}
              variant="outline"
              className="border-cyan-500/40 hover:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-xs h-8"
              data-testid="retry-stuck-all-btn"
              title="Reintenta todas las facturas atascadas en PROCESSING reusando sus e-NCFs originales"
            >
              <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Reintentar {procCount} atascadas
            </Button>
          );
        })()}
        <div className="relative flex-1 min-w-[260px] max-w-md ml-auto">
          <Search size={14} className="absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por # Trans, e-NCF, RNC, Razón Social, Monto…"
            className="w-full bg-card border border-border rounded-lg pl-9 pr-9 py-2 text-xs"
            data-testid="ecf-search-input"
            aria-label="Buscar facturas electrónicas"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-2 p-1 rounded hover:bg-muted text-muted-foreground"
              aria-label="Limpiar búsqueda"
              data-testid="ecf-search-clear"
            >
              <XCircle size={14} />
            </button>
          )}
          {search && (
            <span
              className="block mt-1 text-[10px] text-muted-foreground"
              data-testid="ecf-search-count"
            >
              {filteredBills.length} resultado{filteredBills.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {/* Action buttons — "Reintentar Todas" EXCLUYE contingencia manual (Uber Eats/PedidosYa) */}
      {summary.contingencia > 0 && (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleRetryAll}
              disabled={loading}
              variant="outline"
              className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
              data-testid="retry-all-contingencias-btn"
            >
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Reintentar Todas las Contingencias ({summary.contingencia || 0})
            </Button>
          </div>
          {(summary.contingencia_manual || 0) > 0 && (
            <p className="text-[10px] text-muted-foreground">
              <Ban size={10} className="inline mr-1" />
              {summary.contingencia_manual} contingencia(s) manual(es) (Uber Eats / PedidosYa) NO se incluyen en el lote — reintentarlas individualmente si aplica.
            </p>
          )}
        </div>
      )}

      {/* 🔒 FIX-004-RECUPERABLE-AUTO-2026-06-25 — Botón ADMIN para drenar el
          backlog en estado duro (Error/REINTENTAR/REJECTED). Reusa los
          pipelines /resend y /emit-pending respetando los candados. El poller
          confirma los EN_PROCESO automáticamente cuando DGII acepta. */}
      {isAdmin && (
        <div className="flex flex-col gap-1">
          <Button
            onClick={handleReprocessBacklog}
            disabled={reprocessing}
            variant="outline"
            className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 self-start"
            data-testid="reprocess-backlog-btn"
            title="Re-disparar el envío de todas las facturas atascadas. Respeta los candados (no toca lo aprobado)."
          >
            <Zap size={14} className={`mr-2 ${reprocessing ? 'animate-pulse' : ''}`} />
            {reprocessing ? 'Reprocesando rezago…' : 'Reprocesar Rezago e-CF'}
          </Button>
          <p className="text-[10px] text-muted-foreground">
            Drena en lotes (≤50) los e-CF en Error/REINTENTAR/REJECTED reusando o asignando e-NCF según corresponda. El poller confirma los EN_PROCESO cuando DGII acepta.
          </p>
        </div>
      )}

      {/* Bills Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="text-left p-3">Trans.</th>
                <th className="text-left p-3">Fecha</th>
                <th className="text-left p-3">e-NCF</th>
                <th className="text-left p-3">Tipo</th>
                <th className="text-right p-3">Total</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Detalle</th>
                <th className="text-center p-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredBills.map((b, i) => {
                const status = getStatus(b);
                const statusInfo = STATUS_MAP[status] || STATUS_MAP.ERROR;
                const StatusIcon = statusInfo.icon;
                return (
                  <tr key={i} className="border-b border-border/30 hover:bg-muted/30">
                    <td className="p-3 font-mono font-bold">T-{b.transaction_number}</td>
                    <td className="p-3 text-xs">{formatDate(b.paid_at)}</td>
                    <td className="p-3 font-mono text-xs">{b.ecf_encf || '—'}</td>
                    <td className="p-3"><Badge variant="outline" className="text-xs">{b.ecf_type || '—'}</Badge></td>
                    <td className="p-3 text-right font-oswald font-bold">RD$ {(b.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                    <td className="p-3">
                      <Badge className={`text-xs ${statusInfo.color}`}>
                        <StatusIcon size={10} className={`mr-1 ${status === 'PROCESSING' ? 'animate-spin' : ''}`} />{statusInfo.label}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">
                      {b.ecf_reject_reason || b.ecf_error || (b.ecf_provider ? `via ${b.ecf_provider}` : '') || (b.ecf_security_code ? `Código: ${b.ecf_security_code}` : '—')}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleRefreshStatus(b.id)} title="Actualizar status" className="p-1.5 rounded-lg hover:bg-muted transition-all min-w-[32px] min-h-[32px]">
                          <Search size={14} className="text-blue-500" />
                        </button>
                        {/* 🔒 BUG-RETRY-STUCK-2026-05 — boton "Reintentar" para PROCESSING reusando mismo e-NCF (solo admin) */}
                        {status === 'PROCESSING' && isAdmin && (
                          <button
                            onClick={() => handleRetryStuck(b.id)}
                            title="Reintentar reusando el mismo e-NCF (Multiprod cayó y la factura está atascada)"
                            disabled={loading}
                            className="p-1.5 rounded-lg hover:bg-muted transition-all min-w-[32px] min-h-[32px]"
                            data-testid={`retry-stuck-btn-${b.id}`}
                          >
                            <RefreshCw size={14} className={`text-cyan-500 ${loading ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        {/* 🔒 MULTIPROD-ASYNC-2026-06-23 — Ícono informativo para EN_PROCESO.
                            La factura fue firmada por Multiprod y está en su cola DGII.
                            Reintentar manualmente reenviaría un XML que Multiprod ya tiene firmado
                            (Angel lo confirmó: causaría doble procesamiento). El botón muestra
                            info clara al usuario en lugar de un Reintentar engañoso. */}
                        {status === 'EN_PROCESO' && (
                          <button
                            onClick={() => notify.info(
                              `e-NCF ${b.ecf_encf} firmado por Multiprod`,
                              {
                                description: `Está en cola para envío a DGII${b.ecf_id_cola ? ` (id_cola=${b.ecf_id_cola})` : ''}. Se actualizará automáticamente cuando DGII confirme estado final. No reintentar.`,
                                duration: 10000,
                              }
                            )}
                            title="Firmado por Multiprod, esperando confirmación DGII"
                            className="p-1.5 rounded-lg hover:bg-muted transition-all min-w-[32px] min-h-[32px]"
                            data-testid={`ecf-en-proceso-info-${b.id}`}
                          >
                            <Clock size={14} className="text-indigo-500" />
                          </button>
                        )}
                        {(status === 'CONTINGENCIA' || status === 'REJECTED') && (
                          <>
                            <button 
                              onClick={() => setEditDialog({ open: true, bill: b, newType: b.ncf?.replace('PENDING-', '') || 'E32' })} 
                              title="Editar tipo e-CF" 
                              className="p-1.5 rounded-lg hover:bg-muted transition-all min-w-[32px] min-h-[32px]"
                              data-testid="edit-ecf-type-btn"
                            >
                              <Pencil size={14} className="text-purple-500" />
                            </button>
                            <button onClick={() => handleRetry(b.id)} title="Reintentar" disabled={loading} className="p-1.5 rounded-lg hover:bg-muted transition-all min-w-[32px] min-h-[32px]" data-testid={`retry-btn-${b.id}`}>
                              <RefreshCw size={14} className={`text-amber-500 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                          </>
                        )}
                        {status === 'CONTINGENCIA_MANUAL' && (
                          <>
                            <span
                              className="text-[10px] px-2 py-1 rounded bg-gray-500/10 text-gray-500 dark:text-gray-400"
                              title="Plataforma externa (Uber Eats/PedidosYa) — no se envía en lote. Usa el botón Reintentar solo si decides forzar el envío manualmente."
                              data-testid="manual-contingency-note"
                            >
                              Plataforma externa
                            </span>
                            <button
                              onClick={() => handleRetry(b.id)}
                              title="Reintentar manualmente (forzar envío a DGII)"
                              disabled={loading}
                              className="p-1.5 rounded-lg hover:bg-muted transition-all min-w-[32px] min-h-[32px]"
                              data-testid={`retry-btn-${b.id}`}
                            >
                              <RefreshCw size={14} className={`text-amber-500 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                          </>
                        )}
                        {/* 🔒 ECF-EMIT-PENDING-2026-06-24 — Botón para emitir
                            e-CF en bills paid SIN e-NCF (pipeline original no
                            disparó). Visible SOLO cuando isPendingEmission(b).
                            Doble candado: backend rechaza si tiene ecf_encf o
                            ecf_status; frontend deshabilita durante la llamada. */}
                        {isPendingEmission(b) && (
                          <button
                            onClick={() => handleEmitPending(b)}
                            title="Emitir e-CF (factura paid sin e-NCF)"
                            disabled={emittingId === b.id}
                            className="p-1.5 rounded-lg hover:bg-emerald-500/10 transition-all min-w-[32px] min-h-[32px] disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`emit-pending-btn-${b.id}`}
                          >
                            <Send size={14} className={`text-emerald-500 ${emittingId === b.id ? 'animate-pulse' : ''}`} />
                          </button>
                        )}
                        <button onClick={() => handleReprint(b.id)} title="Reimprimir" className="p-1.5 rounded-lg hover:bg-muted transition-all">
                          <Printer size={14} className="text-muted-foreground" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredBills.length === 0 && (
            <p className="text-center text-muted-foreground py-8" data-testid="ecf-empty-state">
              {search
                ? `No hay facturas que coincidan con "${search}"`
                : 'No hay facturas electrónicas en este filtro'}
            </p>
          )}
        </div>
      </div>

      {/* 📊 Panel de Diagnóstico Multiprod — solo visible para admin (BUG-F8 fix) */}
      {isAdmin && <EcfHealthPanel />}

      {/* Edit e-CF Type Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, bill: null, newType: '' })}>
        <DialogContent className="max-w-sm !z-[9999]" overlayClassName="!z-[9999]">
          <DialogHeader>
            <DialogTitle className="font-oswald">Editar Tipo de e-CF</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Factura <span className="font-bold text-foreground">T-{editDialog.bill?.transaction_number}</span> — 
              RD$ {(editDialog.bill?.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground">
              Tipo actual: <span className="font-mono font-bold">{editDialog.bill?.ecf_encf || editDialog.bill?.ncf || '—'}</span>
            </p>
            <div>
              <label className="text-sm font-medium">Nuevo Tipo de e-CF</label>
              <select 
                value={editDialog.newType} 
                onChange={(e) => setEditDialog({ ...editDialog, newType: e.target.value })}
                className="w-full mt-1 p-2 rounded-lg bg-background border border-border text-sm"
                data-testid="ecf-type-select"
              >
                {ECF_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, bill: null, newType: '' })} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleEditEcfType} disabled={loading} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="save-ecf-type-btn">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Después de guardar, presiona <RefreshCw size={10} className="inline mx-0.5 text-amber-500" /> Reenviar para enviar a DGII
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
// 🔒 EcfHealthPanel — Admin-only diagnostic panel for Multiprod health
// ══════════════════════════════════════════════════════════════════
function EcfHealthPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ecf/health-metrics`, { headers: hdrs() });
      if (r.ok) {
        const d = await r.json();
        setData(d);
      } else if (r.status !== 403) {
        notify.error('Error cargando métricas');
      }
    } catch {
      notify.error('Error de conexión');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && !data) fetchMetrics();
  }, [open]); // eslint-disable-line

  const tierColor = {
    excellent: 'text-emerald-500',
    good: 'text-green-500',
    warning: 'text-amber-500',
    critical: 'text-red-500',
    unknown: 'text-muted-foreground',
  }[data?.health_tier || 'unknown'];

  const tierBg = {
    excellent: 'bg-emerald-500/10 border-emerald-500/30',
    good: 'bg-green-500/10 border-green-500/30',
    warning: 'bg-amber-500/10 border-amber-500/30',
    critical: 'bg-red-500/10 border-red-500/30',
    unknown: 'bg-muted/20 border-border',
  }[data?.health_tier || 'unknown'];

  const tierEmoji = {
    excellent: '🟢',
    good: '🟢',
    warning: '🟡',
    critical: '🔴',
    unknown: '⚪',
  }[data?.health_tier || 'unknown'];

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden"
      data-testid="ecf-health-panel"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-all"
        data-testid="ecf-health-panel-toggle"
      >
        <Activity size={18} className="text-indigo-500" />
        <div className="flex-1 text-left">
          <h3 className="font-oswald font-bold text-sm uppercase tracking-wide">
            Diagnóstico Multiprod · Últimas 24h
          </h3>
          <p className="text-[10px] text-muted-foreground">
            Panel exclusivo para administradores
          </p>
        </div>
        {data && (
          <Badge className={`${tierBg} ${tierColor} text-xs font-bold`}>
            {tierEmoji} {data.acceptance_rate !== null ? `${data.acceptance_rate}%` : 'Sin datos'}
          </Badge>
        )}
        {open ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 py-4 border-t border-border space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-indigo-500" />
              <span className="ml-2 text-sm text-muted-foreground">Cargando métricas...</span>
            </div>
          )}

          {!loading && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className={`rounded-lg border p-3 ${tierBg}`} data-testid="metric-acceptance-rate">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Tasa de aceptación</p>
                  <p className={`font-oswald text-2xl font-bold ${tierColor}`}>
                    {data.acceptance_rate !== null ? `${data.acceptance_rate}%` : '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {data.accepted} de {data.total_sends} envíos
                  </p>
                </div>
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="metric-rejected">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Rechazadas DGII</p>
                  <p className="font-oswald text-2xl font-bold text-red-500">{data.rejected}</p>
                  <p className="text-[10px] text-muted-foreground">Requieren acción manual</p>
                </div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="metric-transient">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Errores transitorios</p>
                  <p className="font-oswald text-2xl font-bold text-amber-500">{data.transient_errors}</p>
                  <p className="text-[10px] text-muted-foreground">Reintentados automáticamente</p>
                </div>
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3" data-testid="metric-response-time">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Tiempo respuesta</p>
                  <p className="font-oswald text-2xl font-bold text-blue-500">
                    {data.response_times?.avg_ms || 0}<span className="text-sm">ms</span>
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    P95: {data.response_times?.p95_ms || 0}ms · {data.response_times?.samples || 0} muestras
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-background/50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={14} className="text-indigo-500" />
                  <h4 className="font-semibold text-xs uppercase tracking-wide">Actividad por hora (24h)</h4>
                </div>
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.hourly_series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.15)" />
                      <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                      <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--background)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          fontSize: 11,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                      <Bar dataKey="accepted" stackId="a" fill="#10b981" name="Aceptadas" />
                      <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rechazadas" />
                      <Bar dataKey="retry" stackId="a" fill="#3b82f6" name="Retry" />
                      <Bar dataKey="error" stackId="a" fill="#f59e0b" name="Error" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-border bg-background/50 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-red-500" />
                    <h4 className="font-semibold text-xs uppercase tracking-wide">
                      Top motivos de rechazo
                    </h4>
                  </div>
                  {(data.top_reject_reasons || []).length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic">
                      Sin rechazos en las últimas 24h 🎉
                    </p>
                  ) : (
                    <ul className="space-y-1.5" data-testid="top-reject-list">
                      {data.top_reject_reasons.map((r, i) => (
                        <li key={i} className="text-[11px] flex items-start gap-2">
                          <span className="font-bold font-mono text-red-500 min-w-[24px]">
                            {r.count}×
                          </span>
                          <span className="text-foreground/80 break-words">{r.motivo}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="rounded-lg border border-border bg-background/50 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={14} className="text-amber-500" />
                    <h4 className="font-semibold text-xs uppercase tracking-wide">
                      Cola de auto-retry
                    </h4>
                  </div>
                  <div className="grid grid-cols-5 gap-1">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const count = data.queue_state?.[`attempt_${n}`] || 0;
                      return (
                        <div
                          key={n}
                          className={`rounded p-2 text-center ${count > 0 ? 'bg-blue-500/15 border border-blue-500/30' : 'bg-muted/20 border border-border'}`}
                          data-testid={`queue-attempt-${n}`}
                        >
                          <p className="font-oswald font-bold text-lg">{count}</p>
                          <p className="text-[9px] text-muted-foreground">#{n}</p>
                        </div>
                      );
                    })}
                  </div>
                  {(data.queue_state?.exhausted || 0) > 0 && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      <Ban size={10} className="inline mr-1" />
                      {data.queue_state.exhausted} agotadas (5/5 intentos fallidos)
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t border-border">
                <span>
                  Generado: {data.generated_at ? new Date(data.generated_at).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'medium' }) : '—'}
                </span>
                <Button
                  onClick={fetchMetrics}
                  disabled={loading}
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  data-testid="refresh-health-metrics"
                >
                  <RefreshCw size={11} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refrescar
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
